import Box from '@mui/material/Box';
import { memo, type ReactElement } from 'react';
import { FunctionArea } from './pdf/FunctionArea';
import { usePdfPreview } from './pdf/hooks/usePdfPreview';
import { PdfCanvas } from './pdf/PdfCanvas';
import { PdfErrorWarning } from './pdf/PdfErrorWarning';
import { SkeletonLoader } from './pdf/SkeletonLoader';

/**
 * PDFプレビューコンポーネント
 *
 * PDFファイルのプレビュー表示と操作機能を提供します。
 * ページ送り、リフレッシュ、ダウンロード機能を含みます。
 *
 * @returns {ReactElement} PDFプレビューコンポーネント
 *
 * @example
 * ```tsx
 * <PdfPreview />
 * ```
 *
 * @since 1.0.0
 * @author Resume Commit Team
 */
/**
 * PDFプレビューコンポーネントのProps型定義
 */
type PdfPreviewProps = {
  isScrolling?: boolean;
  hideControls?: boolean; // 操作エリアを非表示にするプロパティ
  onPageChange?: (page: number) => void; // ページ変更時のコールバック
  onPageTotalChange?: (total: number) => void; // 総ページ数変更時のコールバック
  page?: number; // 外部から制御される現在ページ
  onPdfScrollingChange?: (isScrolling: boolean) => void; // PDFスクロール状態変更時のコールバック
  onRenderingChange?: (isRendering: boolean) => void; // PDFレンダリング状態変更時のコールバック
};

function PdfPreview({
  isScrolling = false,
  hideControls = false,
  onPageChange,
  onPageTotalChange,
  page: controlledPage,
  onPdfScrollingChange,
  onRenderingChange,
}: PdfPreviewProps): ReactElement {
  const {
    canvasRef,
    scrollContainerRef,
    page,
    pageTotal,
    isRendering,
    isFocusMode,
    isInitialState,
    isVisible,
    pdfUrl,
    pdfError,
    isRirekishoMode,
    handlePageChange,
    handleRefresh,
    handleDownloadClick,
  } = usePdfPreview({
    onPageChange,
    onPageTotalChange,
    page: controlledPage,
    onPdfScrollingChange,
    onRenderingChange,
  });

  if (isInitialState && !isFocusMode) {
    // 初回描画実施中の表示（フォーカスモード時はスキップ）
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: { xs: 2, sm: 3, md: 4 },
            boxSizing: 'border-box',
          }}
        >
          <SkeletonLoader />
        </Box>
      </Box>
    );
  }

  // フォーカスモード時は背景を透明にして、PDF表示エリアもPDF操作エリアも非表示にする
  if (isFocusMode) {
    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          position: 'relative',
          backgroundColor: 'transparent',
        }}
      />
    );
  }

  // 初回描画実施後の表示
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* フロート表示のPDF操作エリア */}
      {!hideControls && (
        <FunctionArea
          page={page}
          pageTotal={pageTotal}
          pdfUrl={pdfUrl}
          isScrolling={isScrolling}
          isVisible={isVisible}
          isRirekishoMode={isRirekishoMode}
          onPageChange={handlePageChange}
          onRefresh={handleRefresh}
          onDownload={handleDownloadClick}
        />
      )}

      {/* PDFエラー警告 */}
      {pdfError && <PdfErrorWarning error={pdfError} onRetry={handleRefresh} />}

      {/* PDFキャンバス */}
      <PdfCanvas
        canvasRef={canvasRef}
        scrollContainerRef={scrollContainerRef}
        pdfError={pdfError}
        hideControls={hideControls}
        isRendering={isRendering}
      />
    </Box>
  );
}

export default memo(PdfPreview);
